import { FetchError } from 'ofetch'
import { ZodError } from 'zod'
import { useI18n } from 'vue-i18n'
import { ErrorResponseSchema } from '@random-vinyl/backend/schemas'

/**
 * Composable returning a `(code) => message` resolver: localises an error code,
 * falling back to the generic server-error string for any code without a dedicated
 * `errors.*` key. The single home for the te()/t() fallback every error surface needs
 * (StatusBar, the drawers, the views) so the fallback key isn't hardcoded per file.
 */
export function useErrorText(): (code: string | null | undefined) => string {
  const { t, te } = useI18n()
  return code => {
    if (!code) return ''
    const key = `errors.${code}`
    return te(key) ? t(key) : t('errors.ERR_GENERIC_SERVER_ERROR')
  }
}

/**
 * A 409 from the API means the write lost a race with current state — the record
 * changed elsewhere (e.g. sold on Discogs) so the requested move is no longer
 * legal. The UI treats this as "resync, don't just error": refetch so the
 * display snaps to reality. The cache is never the source of truth — see
 * `docs/adr/0001-catalogue-read-model.md`.
 */
export function isConflictError(error: unknown): boolean {
  return error instanceof FetchError && error.statusCode === 409
}

/** Convert any thrown value into a stable error code string for i18n lookup. */
export function toErrorCode(error: unknown): string {
  if (error instanceof FetchError) {
    // Validate the response body against the backend's actual error envelope
    // rather than an inline cast — defends against an upstream/gateway 500
    // that returns text/HTML or a different JSON shape.
    const parsed = ErrorResponseSchema.safeParse(error.data)
    if (parsed.success) return parsed.data.error.code
    if (error.statusCode) return `ERR_HTTP_${error.statusCode}`
  }
  if (error instanceof ZodError) return 'ERR_RESPONSE_SHAPE_MISMATCH'
  if (error instanceof Error && error.message.startsWith('ERR_')) return error.message
  if (typeof error === 'string' && error.startsWith('ERR_')) return error
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 'ERR_NETWORK_OFFLINE'
  return 'ERR_GENERIC_SERVER_ERROR'
}
